import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import SeoMeta from '../../../components/SeoMeta/SeoMeta';
import { useProductBackHandler } from '../../../components/ProductBackButton/ProductBackProvider';
import { useScrollDocumentToTop } from '../../../lib/scrollDocumentToTop';
import { quizGeneratorAPI } from '../../quiz-generation/api/quizGeneratorAPI';
import QuizDisplaySection from '../../quiz-generation/components/QuizDisplaySection';
import { buildQuizTracking } from '../../quiz-generation/utils/buildQuizTracking';
import { uploadedDocumentIds } from '../../quiz-generation/utils/uploadedDocumentIds';
import { PLAN_QUIZ_TOPIC_MAX_CHARS, readPlanParam } from '../../../lib/planLaunchParams';
import '../../quiz-generation/components/QuizGeneratorPage.css';
import './CustomQuizGeneratorPage.css';

const CustomQuizGeneratorPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // ?topic= from a Paths plan item prefills the topic focus once, on
  // load. It never uploads or generates: the student still picks files
  // and clicks Generate. A topic that is blank or over the limit is not
  // truncated into a different topic; the page says it could not use it.
  const [planTopic] = useState(() => readPlanParam(searchParams, 'topic', {
    maxLength: PLAN_QUIZ_TOPIC_MAX_CHARS,
  }));
  const [view, setView] = useState('setup');
  // Generate sits at the bottom of the form. The quiz replaces that form
  // on the same route, so keep the new view at the top.
  useScrollDocumentToTop(view);
  const [specFile, setSpecFile] = useState(null);
  const [pastPaperFile, setPastPaperFile] = useState(null);
  // The upload of the files currently picked: { cacheKey, documentIds }.
  // Files are uploaded (and, signed in, saved to the student's documents)
  // once per pick; every later Generate reuses this until a file changes.
  const [upload, setUpload] = useState(null);
  // Bumped to clear the file inputs when the student must pick again.
  const [fileInputsKey, setFileInputsKey] = useState(0);
  const cacheKey = upload?.cacheKey || null;
  const [uploadStatus, setUploadStatus] = useState(null);
  const [quizStatus, setQuizStatus] = useState(null);
  const [numQuestions, setNumQuestions] = useState(3);
  const [topicFocus, setTopicFocus] = useState(() => (planTopic.status === 'ok' ? planTopic.value : ''));
  const [isLoading, setIsLoading] = useState(false);
  const [currentQuiz, setCurrentQuiz] = useState(null);
  const [userAnswers, setUserAnswers] = useState({});
  // The tracking session generateQuiz started (null when signed out or its start failed).
  const [sessionId, setSessionId] = useState(null);

  useEffect(() => {
    document.title = 'Custom Quiz Generator | PathBuddy';
  }, []);

  const handleFileChange = (e, type) => {
    const file = e.target.files?.[0] || null;
    if (type === 'spec') setSpecFile(file);
    else setPastPaperFile(file);
    // A new pick is new material: the next Generate uploads it.
    setUpload(null);
    setUploadStatus(null);
  };

  // Drop the upload and the picked files so the student chooses them again.
  const clearPickedFiles = () => {
    setUpload(null);
    setSpecFile(null);
    setPastPaperFile(null);
    setFileInputsKey((k) => k + 1);
  };

  const processUploads = async () => {
    if (!specFile || !pastPaperFile) {
      setUploadStatus({ type: 'error', message: 'Please select both files.' });
      return null;
    }

    setUploadStatus({ type: 'info', message: 'Processing your materials…' });
    const result = await quizGeneratorAPI.uploadFiles(specFile, pastPaperFile);
    if (result.success && result.cache_key) {
      const done = { cacheKey: result.cache_key, documentIds: uploadedDocumentIds(result) };
      setUpload(done);
      setUploadStatus({ type: 'success', message: result.message || 'Files processed successfully.' });
      setQuizStatus(null);
      return done;
    }
    setUpload(null);
    setUploadStatus({ type: 'error', message: result.error || 'Upload failed.' });
    return null;
  };

  const uploadFiles = async () => {
    if (!specFile || !pastPaperFile) {
      setUploadStatus({ type: 'error', message: 'Please select both files.' });
      return null;
    }

    if (upload) return upload;

    try {
      setIsLoading(true);
      return await processUploads();
    } catch (error) {
      setUpload(null);
      setUploadStatus({ type: 'error', message: error?.data?.error || error.message || 'Upload failed.' });
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const generateQuiz = async () => {
    if (!specFile || !pastPaperFile) {
      setQuizStatus({ type: 'error', message: 'Select both files, click Upload & process, then Generate.' });
      return;
    }

    try {
      setIsLoading(true);
      setQuizStatus({
        type: 'info',
        message: upload ? 'Generating your quiz…' : 'Processing files and generating your quiz…',
      });

      // Upload only when these files have not been uploaded yet.
      const current = upload || await processUploads();
      if (!current) {
        setQuizStatus({ type: 'error', message: 'Could not process files. Fix the upload error above, then try again.' });
        return;
      }

      const result = await quizGeneratorAPI.generateQuiz(
        current.cacheKey,
        parseInt(numQuestions, 10),
        topicFocus,
        current.documentIds
      );
      if (result.success) {
        setQuizStatus({ type: 'success', message: 'Quiz ready.' });
        setCurrentQuiz(result.questions);
        setSessionId(result.session_id || null);
        setUserAnswers({});
        setView('quiz');
      } else {
        setQuizStatus({ type: 'error', message: result.error || 'Generation failed.' });
      }
    } catch (error) {
      const msg = error?.data?.error || error.message || 'Generation failed.';
      if (error?.status === 422) {
        // A file this upload linked is not the student's: pick the files
        // again. The Generate panel hides with the files, so the message
        // goes under the upload.
        clearPickedFiles();
        setQuizStatus(null);
        setUploadStatus({
          type: 'error',
          message: "We couldn't use one of your uploaded files for this quiz. Choose your files again, then Generate quiz.",
        });
      } else if (/upload files first/i.test(msg)) {
        // The processed files have expired: the next Generate uploads them again.
        setUpload(null);
        setQuizStatus({
          type: 'error',
          message: 'Session expired — click Upload & process again, then Generate quiz.',
        });
      } else {
        setQuizStatus({ type: 'error', message: msg });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = useCallback(() => {
    if (view === 'quiz') {
      setCurrentQuiz(null);
      setUserAnswers({});
      setView('setup');
      return;
    }
    navigate('/apps');
  }, [view, navigate]);

  useProductBackHandler(handleBack);

  const submitQuiz = async () => {
    if (sessionId) {
      try {
        await quizGeneratorAPI.submitCustomQuiz(sessionId, buildQuizTracking(currentQuiz, userAnswers));
      } catch (error) {
        console.error('Error saving custom quiz:', error);
      }
    }
    setSessionId(null);
    setCurrentQuiz(null);
    setUserAnswers({});
    setView('setup');
  };

  return (
    <div className="cqg-page">
      <SeoMeta
        title="Custom Quiz Generator | PathBuddy"
        description="Upload an exam specification and past paper to generate tailored practice questions with solutions."
        keywords="custom quiz generator, practice questions, exam prep, PathBuddy"
        url="https://pathbuddy.com/custom-quiz-generator"
      />

      {isLoading && (
        <div className="cqg-loading" role="status" aria-live="polite">
          <div className="cqg-spinner" />
          <p>Working on it…</p>
        </div>
      )}

      {view === 'setup' && (
        <>
          <header className="cqg-header cqg-header--no-back">
            <div className="cqg-header-copy">
              <p className="cqg-kicker">PathBuddy</p>
              <h1 className="cqg-title">Custom Quiz Generator</h1>
            </div>
          </header>

          <main className="cqg-main">
            <section className="cqg-hero">
              <h2 className="cqg-hero-title">Build a quiz from your own papers</h2>
              <p className="cqg-hero-sub">
                Upload an exam specification and a past paper. We generate practice questions with answers and explanations.
              </p>
              {planTopic.status === 'ok' && (
                <p className="cqg-status cqg-status--info cqg-plan-topic" role="status">
                  From your plan: the topic focus is set to “{planTopic.value}”.
                </p>
              )}
              {planTopic.status === 'invalid' && (
                <p className="cqg-status cqg-status--error cqg-plan-topic" role="status">
                  {planTopic.echo.trim()
                    ? `We couldn't use the topic "${planTopic.echo}" from your plan (it must be 1–${PLAN_QUIZ_TOPIC_MAX_CHARS} characters of plain text).`
                    : "Your plan's link didn't name a topic."}
                </p>
              )}
            </section>

            <section className="cqg-panel">
              <h3 className="cqg-panel-title">1. Upload materials</h3>
              <div className="cqg-upload-grid">
                <label className={`cqg-drop ${specFile ? 'cqg-drop--ready' : ''}`}>
                  <input
                    key={`spec-${fileInputsKey}`}
                    type="file"
                    accept=".pdf,.txt"
                    onChange={(e) => handleFileChange(e, 'spec')}
                  />
                  <span className="cqg-drop-label">Exam specification</span>
                  <span className="cqg-drop-hint">{specFile ? specFile.name : 'PDF or TXT'}</span>
                </label>
                <label className={`cqg-drop ${pastPaperFile ? 'cqg-drop--ready' : ''}`}>
                  <input
                    key={`paper-${fileInputsKey}`}
                    type="file"
                    accept=".pdf,.txt"
                    onChange={(e) => handleFileChange(e, 'paper')}
                  />
                  <span className="cqg-drop-label">Past paper</span>
                  <span className="cqg-drop-hint">{pastPaperFile ? pastPaperFile.name : 'PDF or TXT'}</span>
                </label>
              </div>
              <button
                type="button"
                className="cqg-btn cqg-btn--primary"
                onClick={uploadFiles}
                disabled={isLoading || !specFile || !pastPaperFile || Boolean(upload)}
              >
                Upload &amp; process
              </button>
              {uploadStatus && (
                <p className={`cqg-status cqg-status--${uploadStatus.type}`}>{uploadStatus.message}</p>
              )}
            </section>

            {(cacheKey || (specFile && pastPaperFile)) && (
              <section className="cqg-panel cqg-panel--generate">
                <h3 className="cqg-panel-title">2. Generate questions</h3>
                <div className="cqg-form-row">
                  <label htmlFor="cqg-num">Number of questions</label>
                  <select
                    id="cqg-num"
                    value={numQuestions}
                    onChange={(e) => setNumQuestions(e.target.value)}
                  >
                    <option value="1">1 question</option>
                    <option value="2">2 questions</option>
                    <option value="3">3 questions</option>
                    <option value="5">5 questions</option>
                  </select>
                </div>
                <div className="cqg-form-row">
                  <label htmlFor="cqg-topic">Topic focus (optional)</label>
                  <input
                    id="cqg-topic"
                    type="text"
                    placeholder="e.g. Calculus, critical reasoning…"
                    value={topicFocus}
                    onChange={(e) => setTopicFocus(e.target.value)}
                  />
                </div>
                <button
                  type="button"
                  className="cqg-btn cqg-btn--primary"
                  onClick={generateQuiz}
                  disabled={isLoading}
                >
                  Generate quiz
                </button>
                {quizStatus && (
                  <p className={`cqg-status cqg-status--${quizStatus.type}`}>{quizStatus.message}</p>
                )}
              </section>
            )}
          </main>
        </>
      )}

      {view === 'quiz' && currentQuiz && (
        <div className="quiz-generator-page cqg-quiz-shell">
          <QuizDisplaySection
            questions={currentQuiz}
            userAnswers={userAnswers}
            setUserAnswers={setUserAnswers}
            onSubmit={submitQuiz}
            onBack={handleBack}
            timeRemaining={0}
            formatTime={() => '00:00'}
            examName="Custom Quiz"
            activeQuizSource={null}
          />
        </div>
      )}
    </div>
  );
};

export default CustomQuizGeneratorPage;
